import type { DragEvent } from 'react';
import {
  Briefcase,
  Building2,
  Calendar,
  CheckSquare,
  Edit3,
  Hash,
  Mail,
  PenLine,
  Type,
  User,
} from 'lucide-react';
import type { DocumentSignFieldInput } from '@/lib/api';
import { DEFAULT_FIELD_FRAC } from './fieldPlacement';

type FieldType = DocumentSignFieldInput['type'];

/** Fallback page width used only when a caller (e.g. an isolated unit test)
 *  doesn't pass a measured width; the live editor always passes the real one. */
const FALLBACK_PAGE_WIDTH = 720;

/**
 * Give a palette drag its own drag image sized to the field's real placed
 * footprint (and centred on the cursor, matching the click-to-place ghost and
 * the centre-on-drop maths). Without this the browser uses the full-size
 * palette button as the drag image, so the field appears to "shrink" on drop
 * and is hard to position. Mirrors FieldGhostLayer's dashed-blue styling.
 */
function applyFieldDragImage(e: DragEvent<HTMLElement>, type: FieldType, pageWidth: number) {
  // happy-dom (tests) and older drag mocks don't implement setDragImage.
  if (typeof e.dataTransfer.setDragImage !== 'function') return;
  const frac = DEFAULT_FIELD_FRAC[type];
  const w = Math.round(frac.w * pageWidth);
  // Height fractions are relative to the (taller) page; approximate the aspect
  // the same way the hover ghost does so all previews agree.
  const h = Math.round(frac.h * pageWidth * 1.3);
  const ghost = document.createElement('div');
  ghost.style.cssText =
    `position:absolute;top:-1000px;left:-1000px;box-sizing:border-box;` +
    `width:${w}px;height:${h}px;border:1px dashed #2563eb;` +
    `background:rgba(37,99,235,0.15);border-radius:4px;`;
  document.body.appendChild(ghost);
  e.dataTransfer.setDragImage(ghost, w / 2, h / 2);
  // Remove once the browser has snapshotted the image for this drag.
  setTimeout(() => ghost.remove(), 0);
}

/**
 * dataTransfer key carrying the field type while a palette item is dragged
 * onto a document page. Read by the editor's drop handler.
 */
export const FIELD_DRAG_MIME = 'application/x-document-sign-field';

interface FieldPaletteProps {
  /** Currently-armed type, or `null` when nothing is picked up. */
  selectedType: FieldType | null;
  onSelect: (type: FieldType) => void;
  /** Rendered width of the document page, so a dragged field's preview matches
   *  the size it will be when dropped. */
  pageWidth?: number;
}

const TYPES: { type: FieldType; label: string; icon: typeof Edit3 }[] = [
  { type: 'signature', label: 'Signature', icon: PenLine },
  { type: 'initial', label: 'Initial', icon: Edit3 },
  { type: 'date_signed', label: 'Date Signed', icon: Calendar },
  { type: 'signer_name', label: 'Name', icon: User },
  { type: 'email', label: 'Email', icon: Mail },
  { type: 'company', label: 'Company', icon: Building2 },
  { type: 'title', label: 'Title', icon: Briefcase },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

/**
 * Field-type picker — a single-column rail beside the document, switching to
 * two columns when the layout narrows and the document stacks below it.
 *
 * Two ways to place a field: click a type to arm it (a preview then follows
 * the cursor; the next click on a page drops it), or drag a type straight
 * onto a page. The armed type is highlighted and announced via `aria-pressed`.
 */
export function FieldPalette({
  selectedType,
  onSelect,
  pageWidth = FALLBACK_PAGE_WIDTH,
}: FieldPaletteProps) {
  return (
    <ul role="toolbar" aria-label="Field types" className="grid grid-cols-2 gap-1 md:grid-cols-1">
      {TYPES.map(({ type, label, icon: Icon }) => {
        const active = selectedType === type;
        return (
          <li key={type}>
            <button
              type="button"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(FIELD_DRAG_MIME, type);
                e.dataTransfer.effectAllowed = 'copy';
                applyFieldDragImage(e, type, pageWidth);
              }}
              onClick={() => onSelect(type)}
              aria-pressed={active}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card hover:bg-accent border-transparent'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export type { FieldType };
